import React, {useRef} from "react";
import {motion, useInView, useScroll, useTransform} from "framer-motion";

import AbqBack from "../assets/Abq-Float-Back.jpg";
import AbqFront from "../assets/Abq-Float-Front.png";
import Arie from "../assets/Arie.jpg";
import LukeDany from "../assets/LukeDany.jpg";
import LukeGraduation from "../assets/LukeGraduation.jpg";
import LukeGraduationUPenn from "../assets/LukeGraduationUPenn.jpg";
import LukeMapware2018 from "../assets/LukeMapware2018.jpg";
import Pride from "../assets/pride.jpg";
import LukeAndRep from "../assets/events/RepJoyGarratt.JPG";
import {Donate} from "../components/Header";
import cn from "classnames";

export function useParallax(value, distance) {
    return useTransform(value, [ 0, 1 ], [ -distance, distance ]);
}

const leftBoxStyle = { boxShadow: " rgba(0, 0, 0, 0.6) 16px 16px" };
const rightBoxStyle = { boxShadow: " rgba(0, 0, 0, 0.6) -16px 16px" };

const CrossImages = ({ img1Src, img2Src }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    return (
        <section className={"flex justify-center gap-8 p-8"} ref={ref}>
            <div
                className={"max-h-[600px] w-1/3"}>
                <motion.div
                    style={{
                        width:      "100%",
                        height:     "100%",
                        transform:  isInView ? "none" : "translateX(200px)",
                        opacity:    isInView ? 1 : 0,
                        transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.1s",
                    }}>
                    <img
                        style={leftBoxStyle}
                        className={
                            "w-full h-full object-cover -scale-x-100"
                        }
                        src={img1Src}
                        alt="LukeGraduation"
                    />
                </motion.div>
            </div>
            <div
                className={"max-h-[600px] w-1/3"}>
                <motion.div
                    style={{
                        width:      "100%",
                        height:     "100%",
                        transform:  isInView ? "none" : "translateX(-200px)",
                        opacity:    isInView ? 1 : 0,
                        transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.65, 1) 0.1s",
                    }}>
                    <img
                        style={rightBoxStyle}
                        className={
                            "h-full w-full object-cover -scale-x-100"
                        }
                        src={img2Src}
                        alt="LukeGraduationUpenn"
                    />
                </motion.div>
            </div>
        </section>
    );
};

const PopIn = ({
	               children,
}) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    return <div ref={ref}
	            className={"flex flex-col items-center justify-center"}
	            style={{
		            transform:  isInView ? "none" : "translateX(-200px)",
		            opacity:    isInView ? 1 : 0,
		            transition: "all 0.6s cubic-bezier(0.17, 0.55, 0.55, 1) 0.1s",
	            }}>
        {children}
    </div>;
};

export const ColorBlock = (props) => {
    const { blockColor = "bg-white", left = true } = props;
    return (
        <span style={left ? leftBoxStyle : rightBoxStyle} className={"flex relative m-8"}>
            <p
	      className={
		      "max-w-[1000px] text-xl text-black z-10 p-4 mx-4 backdrop-blur"
	      }
            >
                {props.children}
            </p>
            <span className={"w-full h-full absolute top-0 right-0 " + blockColor}/>
        </span>
    );
};

const imgClass =
	"w-[80vw] max-w-[540px] max-h-[60vh] object-cover shadow-xl shadow-black/50 scale-x-100";

export function About() {
    return (
      <main
        className={"w-full h-max flex flex-col items-center justify-center"}
      >
        <div
          className={
            "relative min-h-max flex flex-col items-center lg:py-32 py-16 align-bottom w-full bg-sky-200/95 backdrop-blur-sm"
          }
        >
          <div className="text-5xl lg:text-6xl text-center">About Luke</div>
        </div>
        <div
          className={
            "scroll-block z-10 flex flex-col justify-center items-center min-h-max pt-[112px] lg:pt-[96px] w-full gap-8"
          }
        >
          <PopIn>
            <img
              style={leftBoxStyle}
              className={imgClass}
              src={LukeDany}
              alt="Luke and Dany"
            />
            <ColorBlock>
              Luke Jungmann is a New Mexico native, born and raised in
              Albuquerque, now residing in Rio Rancho where he lives with his
              wife, Dany Alfaro, their cats, and their dog Arie.
            </ColorBlock>

            <img
              style={leftBoxStyle}
              className={imgClass + " scale-x-100"}
              src={Arie}
              alt="Arie"
            />
          </PopIn>
        </div>

        <div
          className={
            "scroll-block flex flex-col justify-center items-center min-h-max py-12 gap-8 top-0 left-0"
          }
        >
          <PopIn>
            <ColorBlock blockColor={"bg-blue-200"} left={false}>
              After attending the Albuquerque public schools, Luke received a
              Bachelor’s in Mechanical Engineering from New Mexico Tech and a
              Master's in Robotics Engineering from the University of
              Pennsylvania.
            </ColorBlock>
          </PopIn>
          <CrossImages img1Src={LukeGraduation} img2Src={LukeGraduationUPenn} />
        </div>

        <div
          className={
            "scroll-block flex flex-col justify-center items-center min-h-max py-12 gap-8 top-0 left-0"
          }
        >
          <PopIn>
            <ColorBlock blockColor={"bg-orange-200"}>
              Luke then worked as a Senior Software Engineer for a
              photogrammetry product company called Mapware for 7 years.
            </ColorBlock>
            <img
              style={leftBoxStyle}
              className={imgClass + " -scale-x-100"}
              src={LukeMapware2018}
              alt="LukeMapware2018"
            />
          </PopIn>

          <PopIn>
            <ColorBlock blockColor={"bg-green-100"} left={false}>
              Afterwards, Luke joined a small startup called Undreamt, where he
              wore many hats, including Project Manager, Team Lead, and
              high-level Technical Support. Recently, he has begun working at
              Intel as a Site Automation Engineer.
            </ColorBlock>
            <img
              style={leftBoxStyle}
              className={cn(imgClass, "max-w-[900px]")}
              src={LukeAndRep}
              alt="LukeAndRep"
            />
          </PopIn>

          <PopIn>
            <ColorBlock blockColor={"bg-green-100"} left={false}>
              Luke Jungmann will bring his extensive knowledge and experience to
              the public sector, with the intent to benefit New Mexico’s
              economy, environment, and people. As a young Millennial, he
              understands the challenges facing the State and wants to make New
              Mexico better for his generation and the generations to follow.
            </ColorBlock>
            <img
              style={leftBoxStyle}
              className={cn(imgClass, "max-w-[900px]")}
              src={Pride}
              alt="Pride"
            />
          </PopIn>
        </div>
        <div className={"pb-8 w-full flex justify-center"}>
          <div
            className={
              "flex flex-col md:flex-row gap-5 text-center items-center z-100 h-max w-max relative justify-center bg-white m-8 px-8 py-2 rounded"
            }
          >
            <span>
              Every donation helps us fight for{" "}
              <b>
                New Mexican Universal Healthcare, Affordable Housing, Protecting
                our Environment, and more.
              </b>
            </span>
            <Donate />
          </div>
        </div>
      </main>
    );
}
